import { useLocation, useNavigate } from 'react-router-dom';
import currency from "currency.js";
import { Constants, helpers, Enums } from "@shared";
import { useSessionContext } from '@contexts';
import { useAllowedPaymentTypes } from "@hooks";
import { RepeatIcon, PencilIcon } from "@icons";
import ActivePayors from "./active-payors";
import PayorPaymentStatus from "./payor-payment-status";
import { Badge, Button } from "@common";
import type { CurrentBill } from '@app-types';

interface BillProps {
    currentBill: CurrentBill;
}

const Bill = ({ currentBill }: BillProps) => {

    const navigate = useNavigate(), location = useLocation();
    const { session, accounts, selectedAccount } = useSessionContext();
    const {
        allowedPaymentTypes,
        isLoading: allowedPaymentTypesIsLoading,
        isError: allowedPaymentTypesIsError
    } = useAllowedPaymentTypes();

    if (!session) return null;

    const {
        idPayor,
        creditChecked,
        totalPayors,
        paymentPreferences: { hasDefaultPaymentMethod: _hasDefaultPaymentMethod, hasDefaultPaymentMethodDisabled, hasSecondaryPaymentMethod, defaultPaymentMethod },
        accountPreferences: { isAutoPay }
    } = session;

    const defaultPaymentMethodDisabled =
        hasDefaultPaymentMethodDisabled ||
        (!allowedPaymentTypesIsError &&
            !allowedPaymentTypesIsLoading &&
            defaultPaymentMethod?.idPaymentType !== undefined &&
            !allowedPaymentTypes.includes(defaultPaymentMethod.idPaymentType));

    const hasDefaultPaymentMethod =
        _hasDefaultPaymentMethod && !defaultPaymentMethodDisabled;

    const {
        balance,
        dueDate,
        invitations,
        idProductType
    } = currentBill;

    const month = helpers.monthName(dueDate);
    const payorInvitation = invitations.find(i => i.idPayor === idPayor);
    const { isLate, isDue, isPaymentScheduled, isGuarantor = false } = payorInvitation || {};
    const invoiceBalance = currency(balance);

    // Check if this is a split payment scenario and if current user has paid fully
    const isSplitPayment = invitations.length > 1;
    const currentUserPaid = payorInvitation?.balance === 0;

    const resolveDefaults = selectedAccount
        ? helpers.shouldResolveDefaults(accounts, selectedAccount)
        : false;

    const onPayClick = () => {
        const paymentFlowOpts = [
            Constants.Flows.makePayment.id,
            {
                hasDefaultPaymentMethod,
                isAutoPay,
                resolveDefaults
            },
            {
                currentBill,
                account: { ...session }
            }
        ] as const;

        if (!hasDefaultPaymentMethod && hasSecondaryPaymentMethod) {
            // if they have a secondary method we go to the settings page (methods)
            // so they can select that one as the default
            // and the flow simply ends at that point, they need to go back to dashboard
            // to enter payment flow again
            helpers.navigateTo(`/payment-method-settings`, location, navigate, undefined, {
                // TODO ideally state would be typed
                onClickPaymentFlow: paymentFlowOpts
            });
        } else {
            helpers.navigateStart(location, navigate, ...paymentFlowOpts);
        }
    };

    const paymentEnabled = (idProductType === Enums.ProductType.Gateway || (idProductType === Enums.ProductType.SunboundPay && creditChecked));
    const showPastDue = isDue && !isLate && !isPaymentScheduled;

    const handleAutopayClick = () => {
        navigate(`/payment-method-settings`);
    };

    return (
        <>
            <div className="flex flex-col gap-6 items-center">
                {/* Balance Header */}
                <div className="flex flex-col gap-4 items-start w-full">
                    <div className="flex flex-col gap-1 items-start w-full">
                        <h2 className="font-medium text-[20px] leading-6 text-neutral-950 tracking-[0.2px] text-center w-full">
                            {month} Balance
                        </h2>
                        <div className="flex gap-2 items-center justify-center w-full">
                            {(isLate || showPastDue) && (
                                <p id={isLate ? `late-alert` : `pastdue-alert`} className="text-sm leading-5 text-red-700 tracking-[0.14px] text-center">
                                    {isLate ? `Your payment is late.` : `Your payment is past due.`}
                                </p>
                            )}
                            {isAutoPay && (
                                <button
                                    onClick={handleAutopayClick}
                                    className="flex gap-1 items-center focus-visible:outline-focus-ring group"
                                    aria-label="Autopay is On - click to go to Autopay settings">
                                    <Badge color="green" icon={<RepeatIcon className="h-4 w-4" />}>
                                        Autopay
                                    </Badge>
                                    <div className="btn-icon-xs btn-variant-tertiary group-hover:bg-neutral-50">
                                        <PencilIcon />
                                    </div>
                                </button>
                            )}
                            {!isAutoPay && (
                                <button
                                    onClick={handleAutopayClick}
                                    className="flex gap-2 items-center focus-visible:outline-focus-ring group"
                                    aria-label="Autopay is Off - click to go to Autopay settings">
                                    <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px] text-center">
                                        Autopay is Off
                                    </p>
                                    <div className="btn-icon-xs btn-variant-tertiary group-hover:bg-neutral-50">
                                        <PencilIcon />
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>
                    <p id="invoice-balance" className="font-medium text-[48px] leading-[56px] text-neutral-950 tracking-[0.48px] text-center w-full">
                        {invoiceBalance.format()}
                    </p>
                </div>

                {/* Payment Status / Make Payment Button */}
                <div className="flex flex-col gap-4 w-full md:w-[420px]">
                    {isSplitPayment && (
                        <PayorPaymentStatus
                            invitations={invitations}
                            currentPayorId={idPayor}
                        />
                    )}
                    {(!isSplitPayment || !currentUserPaid) && (
                        <Button
                            variant="primary"
                            size="xl"
                            disabled={!paymentEnabled}
                            onClick={onPayClick}
                            className="w-full"
                        >
                            Make a Payment
                        </Button>
                    )}
                </div>

                {/* Active Payors Section */}
                <div className="w-full md:w-[420px]">
                    <ActivePayors
                        invitations={invitations}
                        currentPayorId={idPayor}
                        isGuarantor={isGuarantor}
                        currentBill={currentBill}
                        totalAccountPayors={totalPayors}
                    />
                </div>
            </div>
        </>
    );
};

export default Bill;
